import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export default function MarkupDialog({ open, context, users, onSave, onClose }) {
  const [comment, setComment] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [severity, setSeverity] = useState("comment");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setComment(""); setOwnerId(""); setSeverity("comment"); }
  }, [open]);

  const save = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await onSave({ comment: comment.trim(), owner_id: ownerId || null, severity });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] max-w-md" data-testid="markup-dialog">
        <DialogHeader>
          <DialogTitle className="text-[var(--c-text)]">
            New markup {context?.kind === "3d" ? "on the 3D model" : "on the drawing"}
            {context?.target ? ` — ${context.target}` : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <textarea data-testid="markup-comment-input" rows={3} value={comment} autoFocus
            onChange={(e) => setComment(e.target.value)}
            placeholder="Comment / instruction for the reviewer or shop floor…"
            className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-3 py-2 text-sm text-[var(--c-text)] focus:border-[var(--c-accent)] outline-none" />
          <div className="flex gap-2">
            <Select value={ownerId} onValueChange={setOwnerId}>
              <SelectTrigger data-testid="markup-owner-select"
                className="flex-1 h-8 bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
                <SelectValue placeholder="Assign owner (optional)" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                {(users || []).map((u) => (
                  <SelectItem key={u.id} value={u.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">
                    {u.name} ({u.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger data-testid="markup-severity-select"
                className="w-32 h-8 bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                {["comment", "important", "critical"].map((s) => (
                  <SelectItem key={s} value={s} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="markup-cancel-btn" onClick={onClose}
              className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-8 text-xs">
              Cancel
            </Button>
            <Button data-testid="markup-save-btn" onClick={save} disabled={!comment.trim() || busy}
              className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
              {busy ? "Saving…" : "Add markup"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
